// table-control.component.ts
import { Component, Input, Output, EventEmitter, SimpleChanges, OnChanges, input, Pipe, PipeTransform, inject, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TableColumn } from 'src/app/models/TableColumn';
import { Formatter } from './formatter.util';
import { AlertService } from '../../services/alert.service';
import { InventoryService } from 'src/app/services/inventory.service';
import { saveAs } from 'file-saver';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipDirective } from '../../../directives/tooltip.directive';
import { SafeHtmlPipe } from '../../../pipes/safeHtml.pipe';
import { PageableRequest, PageableResponse, PageArray } from '../../../models/ApiResponse';
import { DataFile } from '../../../models/DataFile.model';
import { createAwaitable } from '../../AwaitableObservable';

export type TOnLoadPage = { req: PageableRequest | any, setData: ((data: PageableResponse<any> | PageArray<any> | any) => void) };

export type TButtonAction = {
  label: string | ((row: any) => string);
  class: string | ((row: any) => string);
  icon?: string | ((row: any) => string);
  tooltip?: string | ((row: any) => string);
  showLabel?: boolean;
  disabled?: boolean | ((row: any) => boolean);
  hidden?: boolean | ((row: any) => boolean);
};

@Component({
  selector: 'app-table-control',
  templateUrl: './table-control.component.html',
  styleUrls: ['./table-control.component.css'],
  imports: [CommonModule, FormsModule, TooltipDirective, SafeHtmlPipe]
})
export class TableControlComponent<T extends Record<string, any>> implements OnChanges {

  //changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);

  private readonly destroyRef = inject(DestroyRef);
  private readonly searchSubject = new Subject<void>();

  @Input() columns: TableColumn[] = [];


  private _data: T[] = [];
  private get _pageData(): PageArray<T> { return this._data as PageArray<T> }

  public pageDataReloadMode : 'loading' | 'loaded' | 'needLoad' = 'needLoad';

  @Input()
  get data(): T[] {
    if (this.isPageable) {
      if (this.serverDataRequest === null) {
        this.pageDataReloadMode === 'needLoad';
      }
      else if (this.pageDataReloadMode === 'needLoad') {
        this.pageDataReloadMode = 'loading';
        const e = {
          req: this.serverDataRequest!,
          setData: (data: PageableResponse<T> | PageArray<T> | any) => {
            this.pageDataReloadMode = 'loaded';
            if (data instanceof Error) {
              // это ошибка - просто выводим её
              this.alertService.error(data.message);
            }
            else if (typeof data === 'string') {
              // это ошибка - просто выводим её
              this.alertService.error(data);
            }
            else if (data) {
              const pr = (data as PageableResponse<T>);
              if (pr.$pageNumber && pr.$pageSize && Array.isArray(data)) {
                // это PageArray<T>
                this._data = data;
              }
              else if (pr.$pageNumber && pr.$pageSize && Array.isArray(pr.data)) {
                // это PagableResponse<T>
                const res = <T[]>pr.data;
                const respr = (res as any as PageableResponse<T>);
                respr.$pageNumber = pr.$pageNumber;
                respr.$pageSize = pr.$pageSize;
                respr.$totalRecords = pr.$totalRecords;
                respr.data = pr.data;
                this._data = respr.data;
              }
              this.currentPage = pr.$pageNumber || 1;
              this.applyFilter();
            }
          }
        };

        e.req.$pageSize = this.pageSize || 10;
        e.req.$pageNumber = this.currentPage || 1;
        delete e.req.$exportAll;
        this.onLoadPage.emit(e);
      }
    }
    return this._data;
  }
  set data(value: T[]) { this._data = !this.isPageable ? value : this._data; }

  private _pageSize = 10;
  @Input() get pageSize() { return this.isPageable ? this.serverDataRequest!.$pageSize || this._pageSize : this._pageSize; }
  set pageSize(value: number) { this._pageSize = value; }

  @Input() showCheckboxes = false;

  @Input() showActions = false;
  @Input() actionButtons: TButtonAction[] = [];

  @Input() isMultiSelect = true;

  @Input() rowClass?: string | ((row: any) => string) | any;

  @Input() set clearSelection(clear: boolean) {
    if (clear) {
      this.globalSelectedItems = [];
      this.selectAll = false;
      this.selectedItemsChange.emit(this.globalSelectedItems);
    }
  }

  @Input() cardViewBreakpoint: number = 768; // Bootstrap's md breakpoint

  @Input() sheetName : string = '';

  @Output() filteredItemsChange = new EventEmitter<T[]>();
  @Output() selectedItemsChange = new EventEmitter<T[]>();
  @Output() actionClick = new EventEmitter<{ action: string; item: T }>();


  /** Признак того, что компонент использует серверную страничную навигацию */
  public get isPageable() { return this.serverDataRequest !== undefined; }

  reloadPage(filter?: string, orderBy?: string) {
    if (this.isPageable) {
      if (this.serverDataRequest) {
        const order = this.currentSortColumn && `${this.isSortAscending ? '' : '-'}${this.currentSortColumn}`;
        this.serverDataRequest.$filter = filter ?? this.searchTerm;
        this.serverDataRequest.$orderBy = orderBy ?? order!;
      } 
      this.pageDataReloadMode = 'needLoad';
    }
  }

  /**
   * Серверная пагинация.
   * Параметры запроса для получения ответа от сервера для отображения в компоненте.
   * Если задано - значение параметра компонента data - игнорируется.
   * Алгоритм работы следующий:
   *   1. Устанавливается значение параметра serverDataRequest
   *   2. Компонент вызывает onLoadPage с установленными значениями полей PagableRequest
   *   3. Выполняется запрос к серверу с указанными параметрами
   *   4. Ответ от сервера должен быть в виде PagableResponse<T> или преобразованным в PageArray<T>.
   *   5. Вызывается метод setData(...), переданный в событии onLoadPage.
   */
  @Input() serverDataRequest?: PageableRequest;

  /**
   * Серверная пагинация.
   * Событие вызывается, когда следует обновить текущую страницу, отображаемую компонентом.
   *
   * Это может произойти при:
   *   - изменении номера отображаемой страницы
   *   - поиска по содержимому таблицы (не реализовано)
   *   - сортировке (не реализовано)
   *
   * Ответ от сервера должен быть в виде PagableResponse<T>, а источнком данных для компонента - PageArray<T>.
   */
  @Output() onLoadPage = new EventEmitter<TOnLoadPage>();



  isMobileView = false;

  currentSortColumn: string | null = null;
  isSortAscending = true;



  visibleColumns: TableColumn[] = [];
  filteredData: T[] = [];
  currentPage = 1;
  searchTerm: string = '';
  selectAll: boolean = false;

  // Track all selected items across filters
  @Input('selectedItems') globalSelectedItems: T[] = [];

  // Track if we're in "select all" mode for current filter
  currentFilterSelectAll = false;

  /**
   *
   */
  constructor(private domSanitizer: DomSanitizer, private alertService: AlertService, private inventoryService: InventoryService,) {
    this.checkViewport();
    window.addEventListener('resize', () => this.checkViewport());
    this.searchSubject.pipe(
      debounceTime(500),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.applyFilter(true));
  }

  getFormattedValue(col: TableColumn, value: any, row: any): string {
    return Formatter.format(value, col, row);
  }

  sanitizer: any = this.domSanitizer;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['searchTerm']) {
      this.applyFilter();
    }
    if (changes['serverDataRequest']) {
      this.reloadPage();
    }
    if (changes['columns']) {
      this.visibleColumns = this.columns.filter(col => !col.hidden);
    }
  }

  checkViewport(): void {

    this.isMobileView = window.innerWidth < this.cardViewBreakpoint;
  }

  get visibleSelectedItems(): T[] {
    return this.globalSelectedItems.filter(item =>
      this.filteredData.includes(item)
    );
  }

  onSearchChange(): void {
    this.searchSubject.next();
  }

  public applyFilter(needReloadData?: boolean): void {
    if (needReloadData && this.isPageable) {
      this.currentPage = 1;
      this.reloadPage();
      return;
    }

    if (!this.searchTerm || this.isPageable) {
      this.filteredData = [...this.data];
      if (!this.isPageable) {
        this.currentPage = 1;
      }
      this.filteredItemsChange.emit([...this.data]);
      return;
    }

    const searchCriteria = this.searchTerm.split(',').map(criterio => criterio.trim().toLowerCase()).filter(value => value.length > 0);

    this.filteredData = this.data.filter((item) =>
      this.columns
      .filter(field => !field.nonFiltered)
      .some(field => {
        const fieldValue = (item as any)[field.key];
        // Verificamos si el valor del campo no es undefined o null
        if (fieldValue !== undefined && fieldValue !== null) {
          return searchCriteria.some((criterio) => fieldValue.toString().toLowerCase().includes(criterio));
        }
        return false; // Si es undefined o null, lo ignoramos
      })
    );



    this.currentPage = 1;
    this.filteredItemsChange.emit([...this.filteredData]);
    this.updateSelectionState();
  }

  get paginatedData(): T[] {
    if (this.isPageable) {
      return this.filteredData;
    } else {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredData.slice(start, start + this.pageSize);
    }
  }

  get totalPages(): number {
    if (this.isPageable) {
      return Math.ceil(this._pageData.$pageSize ? ((this._pageData.$totalRecords || 0) / this._pageData.$pageSize) : 0);
    } else {
      const value = Math.ceil(this.filteredData.length / this.pageSize);
      return value;
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      if (this.isPageable) this.reloadPage();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      if (this.isPageable) this.reloadPage();
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      if (this.isPageable) this.reloadPage();
    }
  }

  getPageRange(): number[] {
    const rangeSize = 5; // Show 5 page numbers at a time
    let start = Math.max(1, this.currentPage - Math.floor(rangeSize / 2));
    const end = Math.min(this.totalPages, start + rangeSize - 1);

    // Adjust start if we're at the end
    start = Math.max(1, end - rangeSize + 1);

    const range = [];
    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  }

  toggleSelectAll(): void {

    // ToDo: isPageable

    if (this.currentFilterSelectAll) {
      // Deselect all currently filtered items
      this.globalSelectedItems = this.globalSelectedItems.filter(
        item => !this.filteredData.includes(item)
      );
    } else {
      // Select all currently filtered items not already selected
      this.filteredData.forEach(item => {
        if (!this.globalSelectedItems.some(selected =>
          this.compareItems(selected, item))
        ) {
          this.globalSelectedItems.push(item);
        }
      });
    }

    this.currentFilterSelectAll = !this.currentFilterSelectAll;
    this.updateSelectionState();
  }

  toggleItemSelection(item: T): void {
    if (this.isMultiSelect) {
      const index = this.globalSelectedItems.findIndex(selected =>
        this.compareItems(selected, item)
      );

      if (index === -1) {
        this.globalSelectedItems.push(item);
      } else {
        this.globalSelectedItems.splice(index, 1);
      }

      this.currentFilterSelectAll = false;
      this.updateSelectionState();

      //this.globalSelectedItems = [];
    }
    else {
      // Single select mode, clear previous selection
      if (this.globalSelectedItems?.length === 1 && this.globalSelectedItems[0] === item) {
        this.globalSelectedItems.length = 0;
      } else if (this.globalSelectedItems) {
        this.globalSelectedItems.length = 0;
        this.globalSelectedItems.push(item);
      } else {
        this.globalSelectedItems = [item];
      }
      this.currentFilterSelectAll = false;
      this.updateSelectionState();
    }

  }

  isSelected(item: T): boolean {
    return this.globalSelectedItems.some(selected =>
      this.compareItems(selected, item)
    );
  }

  // Compare items (customize based on your data structure)
  private compareItems(a: T, b: T): boolean {
    return a === b; // Adjust based on your unique identifier
  }


  private updateSelectionState(): void {
    // Check if all filtered items are selected
    const allFilteredSelected = this.filteredData.every(item =>
      this.globalSelectedItems.some(selected =>
        this.compareItems(selected, item)
      )
    );

    this.currentFilterSelectAll = allFilteredSelected;
    this.selectedItemsChange.emit([...this.globalSelectedItems]);
  }

  onActionClick(action: string, item: T): void {
    this.actionClick.emit({ action, item });
  }

  public sortData(columnKey: string): void {
    if (this.currentSortColumn === columnKey) {
      // Reverse direction if clicking the same column
      this.isSortAscending = !this.isSortAscending;
    } else {
      // New column, default to ascending
      this.currentSortColumn = columnKey;
      this.isSortAscending = true;
    }

    if (this.isPageable) {
      this.reloadPage();
      return;
    }


    this.applySort();
  }

  private applySort(): void {
    if (!this.currentSortColumn) return;

    this.filteredData.sort((a, b) => {
      const valA = a[this.currentSortColumn!];
      const valB = b[this.currentSortColumn!];

      // Handle null/undefined values
      if (valA == null) return this.isSortAscending ? 1 : -1;
      if (valB == null) return this.isSortAscending ? -1 : 1;

      // Numeric comparison
      if (typeof valA === 'number' && typeof valB === 'number') {
        return this.isSortAscending ? valA - valB : valB - valA;
      }

      // String comparison
      return this.isSortAscending
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  }


  public async exportToExcel() {
    try {

      let response: DataFile = null!;

      if (/*false && */this.isPageable) {

        // вызов метода для получения данных экспорта
        this.pageDataReloadMode = 'loading';

        response = await new Promise((resolve, reject) => {
          const e = {
            req: this.serverDataRequest!,

            setData: (data: PageableResponse<T>) => {
              this.pageDataReloadMode = 'loaded';
              if (data instanceof Error) {
                // это ошибка - просто сгенерим её
                reject(data);
              }
              else if (typeof data === 'string') {
                // это ошибка - просто сгенерим её
                reject(new Error(data));
              }
              resolve(data.data as DataFile);
            }
          };

          e.req.$exportAll = true;
          e.req.$sheetName = this.sheetName || 'export';
          e.req.$columns = {};
          this.columns.forEach(col => e.req.$columns![col.key] = col.header);
          this.onLoadPage.emit(e);
          delete e.req.$exportAll;
          delete e.req.$sheetName;
          delete e.req.$columns;
        });
      } else {

        const request: any = {
          sheetName: this.sheetName || 'export',
          data: this.filteredData,
          columns: this.columns.map(col => ({
            key: col.key,
            value: col.header,
          }))
        };
        response = await this.inventoryService.GenericExportToExcel(request);

      }

      const byteCharacters = atob(response.fileContents);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.contentType });
      saveAs(blob, response.fileDownloadName || 'export.xlsx');

    } catch (e) {

      this.alertService.error('Unexpected error. Please try again later.');

    } 
  }

  public getColClass(col: TableColumn, row?: any) {
    let res = '';
    if (col) {
      if (typeof col.cellClass === 'string') {
        res = col.cellClass;
      } else if (typeof col.cellClass === 'function') {
        res = col.cellClass(row[col.key], row) as string;
        if (Array.isArray(res)) res = (res as string[]).join(' ');
      } else if (Array.isArray(col.cellClass)) {
        res = (col.cellClass as string[]).join(' ');
      }
    }
    return res;
  }

  /*
  label: string | ((row: any) => string);
  class: string | ((row: any) => string);
  icon?: string | ((row: any) => string);
  tooltip?: string | ((row: any) => string);
  */


  public getButtonLabel(btn: TButtonAction, row: T): string {
    let res = "";
    if (typeof btn.label === 'string') {
      res = btn.label;
    } else if (typeof btn.label === 'function') {
      res = btn.label(row);
    }
    return res;
  }

  public getButtonClass(btn: TButtonAction, row: T): string {
    let res = "";
    if (typeof btn.class === 'string') {
      res = btn.class;
    } else if (typeof btn.class === 'function') {
      res = btn.class(row);
    }
    return res;
  }

  public getButtonIcon(btn: TButtonAction, row: T): string {
    let res = "";
    if (typeof btn.icon === 'string') {
      res = btn.icon;
    } else if (typeof btn.icon === 'function') {
      res = btn.icon(row);
    }
    return res;
  }

  public getButtonTooltip(btn: TButtonAction, row: T): string {
    let res = "";
    if (typeof btn.tooltip === 'string') {
      res = btn.tooltip;
    } else if (typeof btn.tooltip === 'function') {
      res = btn.tooltip(row);
    }
    return res;
  }

  public isButtonDisabled(btn: TButtonAction, row: T): boolean {
    let res = false;
    if (typeof btn.disabled === 'boolean') {
      res = btn.disabled;
    } else if(typeof btn.disabled === 'function') {
      res = btn.disabled(row);
    }
    return res;
  }

  public isButtonHidden(btn: TButtonAction, row: T): boolean {
    let res = false;
    if (typeof btn.hidden === 'boolean') {
      res = btn.hidden;
    } else if (typeof btn.hidden === 'function') {
      res = btn.hidden(row);
    }
    return res;
  }

  public getRowClass(raw: any): string | undefined {
    return (typeof this.rowClass === 'function') ? this.rowClass(raw) : this.rowClass;
  }

}
